import type { CSSProperties } from 'react'
import type { SxProps } from '@mui/material'
import type { ThemedStyle } from '../types'

/**
 * State container styles
 * Error, success, info, loading, and empty state containers
 */

// Error containers
export const $errorContainer: ThemedStyle<CSSProperties> = ({ colors, spacing, borderRadius }) => {
  // Use errorBackground if available, otherwise create from error color
  const errorBg = (colors as any).errorBackground || `${colors.error}20`
  return {
    marginTop: spacing.sm,
    padding: spacing.sm,
    backgroundColor: errorBg,
    borderRadius: borderRadius.sm,
    color: colors.error,
    fontSize: '14px',
  }
}

export const $sxErrorContainer: ThemedStyle<SxProps> = ({ spacing }) => ({
  mt: `${spacing.md}px`
})

// Export as $errorContainerSx for sx usage
export const $errorContainerSx = $sxErrorContainer

// Success containers
export const $successContainer: ThemedStyle<CSSProperties> = ({ colors, spacing, borderRadius }) => {
  // Use a light green background - success color (not in palette, so hardcoded)
  const successColor = '#4caf50'
  const successLight = `${successColor}20`
  
  return {
    backgroundColor: successLight,
    border: `1px solid ${successColor}`,
    borderRadius: borderRadius.sm,
    padding: spacing.md,
    marginBottom: spacing.md,
    textAlign: 'center',
    color: successColor,
  }
}

// Info box
export const $infoBox: ThemedStyle<CSSProperties> = ({ colors, spacing, borderRadius }) => ({
  padding: spacing.md,
  backgroundColor: colors.palette.input.background,
  borderRadius: borderRadius.sm,
  border: `1px solid ${colors.palette.border}`,
})

// Loading containers
export const $loadingBox: ThemedStyle<CSSProperties> = ({ colors, spacing }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing.lg,
  backgroundColor: colors.background,
})

export const $sxLoadingContainer: ThemedStyle<SxProps> = ({ spacing }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: `${spacing.xl}px`,
})

// Export as $loadingContainer for backward compatibility
export const $loadingContainer = $sxLoadingContainer

export const $sxLoadingSpinner: ThemedStyle<SxProps> = ({ colors }) => ({
  color: colors.primary
})

// Export as $loadingSpinner for backward compatibility
export const $loadingSpinner = $sxLoadingSpinner

// Empty state containers
export const $emptyStateContainer: ThemedStyle<CSSProperties> = ({ colors, spacing }) => ({
  textAlign: 'center',
  padding: spacing.xl,
  backgroundColor: colors.background,
  color: colors.textDim,
})

export const $sxEmptyStateText = (opacity: number = 0.7): ThemedStyle<SxProps> => ({ colors }) => ({
  color: colors.text,
  opacity
})

// Hover effect styles
export const $sxHoverLift: ThemedStyle<SxProps> = () => ({
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
})

export const $sxHoverFade: ThemedStyle<SxProps> = () => ({
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 0.8,
  },
})

export const $sxHoverHighlight: ThemedStyle<SxProps> = ({ colors }) => ({
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: colors.palette.dropdown.hover,
  },
})

// Opacity state styles
export const $sxDisabled: ThemedStyle<SxProps> = () => ({
  opacity: 0.5,
  pointerEvents: 'none',
})

export const $sxMuted: ThemedStyle<SxProps> = () => ({
  opacity: 0.7,
})

export const $sxSecondary: ThemedStyle<SxProps> = () => ({
  opacity: 0.9,
})

// HTML element versions
export const $disabled: ThemedStyle<CSSProperties> = () => ({
  opacity: 0.5,
  pointerEvents: 'none',
})

export const $muted: ThemedStyle<CSSProperties> = () => ({
  opacity: 0.7,
})

export const $hoverLift: ThemedStyle<CSSProperties> = () => ({
  transition: 'all 0.3s ease',
})

export const $hoverFade: ThemedStyle<CSSProperties> = () => ({
  transition: 'opacity 0.3s ease',
})
